/**
 * Cart Drawer Component
 *
 * Slide-out panel showing cart contents with checkout button.
 */

import { useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { useWallet } from '@/hooks/useWallet';
import { getAnimatedThumbnailUrl } from '@/lib/ipfs';

interface CartDrawerProps {
  onCheckout: () => void;
  hideWhenCheckoutOpen?: boolean;
}

export function CartDrawer({ onCheckout, hideWhenCheckoutOpen = false }: CartDrawerProps) {
  const { items, isOpen, closeCart, removeItem, clearCart, totalPriceXtz, itemCount } = useCart();
  const { connect, isConnected } = useWallet();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeCart();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeCart]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getImageUrl = (uri: string | undefined) => {
    if (!uri) return '/placeholder-nft.svg';
    // Use animated thumbnails to preserve GIF animations
    const url = getAnimatedThumbnailUrl(uri, 128);
    return url || '/placeholder-nft.svg';
  };

  const handleCheckout = () => {
    if (!isConnected) {
      connect();
      return;
    }
    onCheckout();
  };

  // Don't render if cart is closed or checkout modal is open
  if (!isOpen || hideWhenCheckoutOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col transform transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cart ({itemCount})
          </h2>
          <button
            onClick={closeCart}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium">Your cart is empty</p>
              <p className="text-sm mt-1">Add items from the marketplace</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.listingId}
                  className="flex gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  {/* Image */}
                  <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <img
                      src={getImageUrl(item.thumbnailUri || item.displayUri)}
                      alt={item.tokenName || `Token #${item.tokenId}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-nft.svg';
                      }}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {item.tokenName || `#${item.tokenId}`}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      via objkt.com
                    </p>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-1">
                      {item.priceXtz} XTZ
                    </p>
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={() => removeItem(item.listingId)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove from cart"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total</span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {totalPriceXtz} XTZ
              </span>
            </div>

            {/* Clear cart */}
            <button
              onClick={clearCart}
              className="w-full text-sm text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
            >
              Clear cart
            </button>

            {/* Checkout button */}
            <button
              onClick={handleCheckout}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnected ? `Checkout (${itemCount} items)` : 'Connect Wallet to Checkout'}
            </button>

            {/* Warning */}
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Items are purchased directly from objkt.com marketplace
            </p>
          </div>
        )}
      </div>
    </>
  );
}
